import { MAX_FORWARD_TARGETS, type ConversationDto, type DeliveryState, type LinkPreviewDto, type MessageDto, type MessageInfoDto, type PinnedMessageDto } from '@dijitalapp/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import { conversationTitle } from '../pages/Messages';
import type { DecryptedMessage } from '../lib/e2ee';
import { EncryptedFileView } from './EncryptedFile';
import { Dialog } from './GroupDialogs';

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

type Translate = ReturnType<typeof useApp>['i18n']['t'];

/** Short text for quotes and pins: the text, or a label for an attachment. */
export function snippet(t: Translate, m: { body: string | null; contentType: MessageDto['contentType'] }): string {
  if (m.body) return m.body.length > 100 ? `${m.body.slice(0, 99)}…` : m.body;
  if (m.contentType === 'encrypted') return t('push.encrypted');
  const key = m.contentType === 'image' ? 'push.photo' : m.contentType === 'voice' ? 'push.voice' : m.contentType === 'video' ? 'push.video' : 'push.file';
  return m.contentType === 'text' ? '' : t(key);
}

export function scrollToMessage(id: string): boolean {
  const el = document.getElementById(`msg-${id}`);
  if (!el) return false;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.classList.add('bubble-flash');
  setTimeout(() => el.classList.remove('bubble-flash'), 1200);
  return true;
}

/** Quote shown above a reply; jumps to the original when it is loaded. */
export function ReplyQuote({ target, name }: { target: MessageDto | undefined; name: (userId: string) => string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (!target || target.deleted) {
    return <div className="reply-quote muted">{target?.deleted ? t('chat.deleted') : t('chat.originalMessage')}</div>;
  }
  return (
    <button type="button" className="reply-quote" onClick={() => scrollToMessage(target.id)}>
      <strong>{name(target.senderId)}</strong>
      <span className="truncate">{snippet(t, target) || t('chat.attachmentLabel')}</span>
    </button>
  );
}

/** Reaction chips under a message; tapping your own emoji removes it, another one switches to it. */
export function ReactionBar({
  message,
  myId,
  name,
  onReact,
}: {
  message: MessageDto;
  myId: string | undefined;
  name: (userId: string) => string;
  onReact: (emoji: string | null) => void;
}) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (message.reactions.length === 0) return null;
  return (
    <div className="reaction-bar">
      {message.reactions.map((r) => {
        const mine = myId !== undefined && r.userIds.includes(myId);
        const names = r.userIds.map((id) => (id === myId ? t('chat.you') : name(id))).join(', ');
        return (
          <button
            key={r.emoji}
            type="button"
            className={`reaction-chip ${mine ? 'mine' : ''}`}
            aria-pressed={mine}
            title={t('chat.reactionsBy', { emoji: r.emoji, names })}
            onClick={() => onReact(mine ? null : r.emoji)}
          >
            <span aria-hidden>{r.emoji}</span> {r.userIds.length}
            <span className="visually-hidden">{t('chat.reactionsBy', { emoji: r.emoji, names })}</span>
          </button>
        );
      })}
    </div>
  );
}

export function QuickReactions({ onPick }: { onPick: (emoji: string) => void }) {
  const { i18n } = useApp();
  return (
    <span className="quick-reactions" role="group" aria-label={i18n.t('chat.react')}>
      {QUICK_REACTIONS.map((e) => (
        <button key={e} type="button" className="link-button" onClick={() => onPick(e)}>
          {e}
        </button>
      ))}
    </span>
  );
}

/** Newest pin first; tapping cycles through the pins and jumps to each one. */
export function PinnedBar({
  pinned,
  canUnpin,
  onUnpin,
  name,
}: {
  pinned: PinnedMessageDto[];
  canUnpin: boolean;
  onUnpin: (messageId: string) => void;
  name: (userId: string) => string;
}) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [index, setIndex] = useState(0);
  if (pinned.length === 0) return null;
  const current = pinned[Math.min(index, pinned.length - 1)]!;
  return (
    <div className="pinned-bar" role="region" aria-label={t('chat.pinned')}>
      <button
        type="button"
        className="pinned-main"
        onClick={() => {
          scrollToMessage(current.messageId);
          setIndex((i) => (i + 1) % pinned.length);
        }}
      >
        <span aria-hidden>📌</span>
        <span className="truncate">
          <strong>{name(current.senderId)}:</strong> {snippet(t, { body: current.preview, contentType: current.contentType })}
        </span>
        {pinned.length > 1 && <span className="hint">{`${Math.min(index, pinned.length - 1) + 1}/${pinned.length}`}</span>}
      </button>
      {canUnpin && (
        <button type="button" className="link-button" onClick={() => onUnpin(current.messageId)}>
          {t('chat.unpin')}
        </button>
      )}
    </div>
  );
}

export function ForwardDialog({ message, onClose }: { message: MessageDto; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const store = useChatStore();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const targets = chat.conversations.filter((c: ConversationDto) => c.canSend);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_FORWARD_TARGETS) next.add(id);
      return next;
    });

  const send = async () => {
    if (!store) return;
    setBusy(true);
    setStatus(null);
    try {
      const r = await store.forward(message.conversationId, message.id, [...selected]);
      if (r.failed.length) setStatus({ kind: 'error', text: t('chat.forwardPartial', { count: r.failed.length }) });
      else onClose();
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('chat.forwardTitle')} onClose={onClose}>
      <p className="hint">{t('chat.forwardHint')}</p>
      <div className="stack" style={{ gap: 0, maxHeight: 320, overflowY: 'auto' }}>
        {targets.map((c) => (
          <label key={c.id} className="toggle-row">
            <span className="truncate">{conversationTitle(c)}</span>
            <span className="spacer" />
            <input
              type="checkbox"
              checked={selected.has(c.id)}
              disabled={!selected.has(c.id) && selected.size >= MAX_FORWARD_TARGETS}
              onChange={() => toggle(c.id)}
            />
          </label>
        ))}
      </div>
      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
      <div className="row">
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" type="button" disabled={selected.size === 0 || busy} onClick={() => void send()}>
          {t('chat.forward')}
        </button>
      </div>
    </Dialog>
  );
}

// ---------- links ----------

const URL_IN_TEXT = /\bhttps?:\/\/[^\s<>"']+/gi;

/** First http(s) URL in a text, without trailing punctuation. */
export function firstUrl(text: string): string | null {
  const m = text.match(URL_IN_TEXT);
  return m?.[0] ? m[0].replace(/[.,;:!?)\]}'"»]+$/, '') : null;
}

/** Message text with http(s) links made clickable; everything else stays plain text (no HTML). */
export function LinkifiedText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_IN_TEXT)) {
    const url = m[0].replace(/[.,;:!?)\]}'"»]+$/, '');
    const start = m.index;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <a key={start} href={url} target="_blank" rel="noopener noreferrer nofollow">
        {url}
      </a>,
    );
    last = start + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export function LinkPreviewCard({ preview, onRemove }: { preview: LinkPreviewDto; onRemove?: () => void }) {
  const { i18n } = useApp();
  const host = URL.canParse(preview.url) ? new URL(preview.url).hostname : '';
  return (
    <div className="link-preview">
      <a href={preview.url} target="_blank" rel="noopener noreferrer nofollow" className="link-preview-body">
        {preview.hasImage && <img src={`/api/v1/link-previews/${preview.id}/image`} alt="" loading="lazy" />}
        <span className="link-preview-text">
          <span className="hint">{preview.siteName ?? host}</span>
          {preview.title && <strong>{preview.title}</strong>}
          {preview.description && <span className="link-preview-desc">{preview.description}</span>}
        </span>
      </a>
      {onRemove && (
        <button type="button" className="link-button" onClick={onRemove}>
          {i18n.t('chat.removePreview')}
        </button>
      )}
    </div>
  );
}

// ---------- message info ----------

export function MessageInfoDialog({ message, onClose }: { message: MessageDto; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [info, setInfo] = useState<MessageInfoDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api<MessageInfoDto>('GET', `/conversations/${message.conversationId}/messages/${message.id}/info`).then(
      (r) => alive && setInfo(r),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [message.conversationId, message.id, t]);
  const groups: { key: DeliveryState; label: string }[] = [
    { key: 'read', label: t('chat.readBy') },
    { key: 'delivered', label: t('chat.deliveredTo') },
    { key: 'sent', label: t('chat.pendingTo') },
  ];
  return (
    <Dialog title={t('chat.infoTitle')} onClose={onClose}>
      <p className="bubble-text">{snippet(t, message) || t('chat.attachmentLabel')}</p>
      {error && <p className="alert alert-error">{error}</p>}
      {!info && !error && <p className="muted">{t('app.loading')}</p>}
      {info?.readsHidden && <p className="hint">{t('chat.readsHidden')}</p>}
      {info &&
        groups.map((g) => {
          const people = info.members.filter((m) => m.status === g.key);
          if (people.length === 0) return null;
          return (
            <section key={g.key} className="stack" style={{ gap: 'var(--space-1)' }}>
              <h3>
                {g.label} ({people.length})
              </h3>
              {people.map((p) => (
                <div key={p.id} className="toggle-row">
                  <strong>{p.displayName}</strong> <span className="muted">@{p.username}</span>
                </div>
              ))}
            </section>
          );
        })}
      <div className="row">
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}

// ---------- end-to-end encrypted messages ----------

/** Decrypts on this device; retries when new room keys arrive (`e2eeVersion`). */
export function EncryptedBody({ message }: { message: MessageDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const chat = useChat();
  const e2ee = chat.e2eeReady ? store?.getE2ee() : null;
  const [result, setResult] = useState<{ key: string; value: DecryptedMessage | null } | null>(null);
  const key = `${message.id}:${chat.e2eeVersion}`;
  useEffect(() => {
    if (!e2ee) return;
    let alive = true;
    void e2ee.decrypt(message).then((value) => alive && setResult({ key, value }));
    return () => {
      alive = false;
    };
  }, [e2ee, message, key]);
  const current = result?.key === key ? result : null;
  if (!current) return <p className="bubble-text muted">{t('e2ee.decrypting')}</p>;
  if (current.value === null) return <p className="bubble-text muted">{t('e2ee.cannotDecrypt')}</p>;
  const { content, unverifiedSender } = current.value;
  return (
    <>
      {content.file ? (
        <EncryptedFileView content={content} />
      ) : (
        <p className="bubble-text">
          <LinkifiedText text={content.body ?? ''} />
        </p>
      )}
      {unverifiedSender && <span className="hint e2ee-warning">⚠ {t('e2ee.unverifiedSender')}</span>}
    </>
  );
}
